import React, { useState } from "react";
import axios from "axios";
import { Icon, IconName } from "../Icon";

interface AccountSettingsProps {
  user: {
    id: string;
    username: string;
    userType: "user" | "premium" | "vip" | "admin";
    approved?: boolean;
  };
  authToken: string;
}

const renderCell = (value: string): React.ReactNode => {
  if (value === "CHECK") {
    return (
      <Icon
        name="check"
        size={14}
        color="#22C55E"
        style={{ verticalAlign: "-2px" }}
      />
    );
  }
  if (value === "CROSS") {
    return (
      <Icon
        name="x"
        size={14}
        color="#EF4444"
        style={{ verticalAlign: "-2px" }}
      />
    );
  }
  if (value.startsWith("CHECK ")) {
    return (
      <>
        <Icon
          name="check"
          size={14}
          color="#22C55E"
          style={{ verticalAlign: "-2px", marginRight: "4px" }}
        />
        {value.slice("CHECK ".length)}
      </>
    );
  }
  return value;
};

const AccountSettings: React.FC<AccountSettingsProps> = ({
  user,
  authToken,
}) => {
  const [processingPayment, setProcessingPayment] = useState(false);

  const handleUpgradeClick = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(
        "/api/subscription/create-checkout-session",
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert("Failed to start upgrade process. Please try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  if (user.userType === "user") {
    // Free User - Show Upgrade Option
    return (
      <div style={{ maxWidth: "800px" }}>
        <h3
          style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}
        >
          Account Overview
        </h3>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "24px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <h4
            style={{
              margin: "0 0 16px 0",
              color: "#9146FF",
              fontSize: "18px",
            }}
          >
            <Icon
              name="rocket"
              size={18}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Upgrade to Premium
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              marginBottom: "20px",
              lineHeight: "1.6",
            }}
          >
            Unlock all personalities and features with HAL8580 Premium!
          </p>

          <div
            style={{
              backgroundColor: "#1a202c",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                fontSize: "36px",
                fontWeight: "bold",
                color: "#9146FF",
                marginBottom: "4px",
              }}
            >
              $4.99
            </div>
            <div style={{ color: "#cbd5e0", fontSize: "14px" }}>per month</div>
          </div>

          {user.approved !== false ? (
            <>
              <button
                onClick={handleUpgradeClick}
                disabled={processingPayment}
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  backgroundColor: processingPayment ? "#4a5568" : "#9146FF",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: processingPayment ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
              >
                {processingPayment ? "Processing..." : "Upgrade Now"}
              </button>

              <p
                style={{
                  textAlign: "center",
                  marginTop: "12px",
                  fontSize: "12px",
                  color: "#718096",
                }}
              >
                Secure payment via Stripe. Cancel anytime.
              </p>
            </>
          ) : (
            <div
              style={{
                backgroundColor: "rgba(245, 158, 11, 0.1)",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, marginTop: "2px" }}
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <h4
                    style={{
                      color: "#f59e0b",
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Account Pending Approval
                  </h4>
                  <p
                    style={{
                      color: "#cbd5e0",
                      margin: 0,
                      fontSize: "13px",
                      lineHeight: "1.5",
                    }}
                  >
                    Upgrades are not available until your account is approved.
                    Due to capacity limitations, your account needs approval
                    first. You will be notified via email when approved.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: "32px" }}>
          <h4
            style={{ color: "#e2e8f0", marginBottom: "20px", fontSize: "18px" }}
          >
            Premium Features
          </h4>
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "24px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <h4
              style={{
                margin: "0 0 20px 0",
                color: "#e2e8f0",
                fontSize: "20px",
                textAlign: "center",
              }}
            >
              <Icon
                name="rocket"
                size={18}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              Unlock HAL’s Full Potential
            </h4>

            <div
              style={{
                backgroundColor: "#1a202c",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #4a5568",
              }}
            >
              {/* Headers */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  backgroundColor: "#2d3748",
                  borderBottom: "2px solid #1a202c",
                }}
              >
                <div
                  style={{
                    padding: "12px 16px",
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#e2e8f0",
                  }}
                >
                  Feature
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "#e2e8f0",
                    backgroundColor: "#374151",
                  }}
                >
                  FREE
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "white",
                    backgroundColor: "#9146FF",
                  }}
                >
                  PREMIUM{" "}
                  <Icon
                    name="sparkles"
                    size={14}
                    color="white"
                    style={{ verticalAlign: "-2px" }}
                  />
                </div>
              </div>

              {/* Comparison Rows */}
              {[
                {
                  feature: "Reasoning",
                  free: "Normal",
                  premium: "Advanced",
                  icon: "brain",
                },
                {
                  feature: "Custom Personality Context",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "smile",
                },
                {
                  feature: "Intelligent Response Detection",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "target",
                },
                {
                  feature: "Automatic Event Responder",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "party-popper",
                },
                {
                  feature: "Intelligent Message Summarization",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "pencil",
                },
                {
                  feature: "Knowledge Base",
                  free: "3 items",
                  premium: "100 items",
                  icon: "book-open",
                },
                {
                  feature: "User Memories",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "message-circle",
                },
                {
                  feature: "Triggers",
                  free: "5",
                  premium: "100",
                  icon: "zap",
                },
                {
                  feature: "Real Time Data",
                  free: "CROSS",
                  premium: "CHECK API",
                  icon: "bar-chart-3",
                },
                {
                  feature: "Daily Summaries",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "trending-up",
                },
                {
                  feature: "Analytics",
                  free: "24 hours",
                  premium: "30 days",
                  icon: "trending-down",
                },
                {
                  feature: "Integrations",
                  free: "RaidRunner",
                  premium: "Patreon, RaidRunner",
                  icon: "plug",
                },
                {
                  feature: "API Access",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "key",
                },
                {
                  feature: "Supporting HAL’s development",
                  free: "CROSS",
                  premium: "CHECK",
                  icon: "heart",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr",
                    borderBottom: index < 13 ? "1px solid #2d3748" : "none",
                  }}
                >
                  {/* Feature name */}
                  <div
                    style={{
                      padding: "10px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#e2e8f0",
                      fontSize: "14px",
                      backgroundColor: "#1a202c",
                    }}
                  >
                    <Icon name={item.icon as IconName} size={16} />
                    <span>{item.feature}</span>
                  </div>
                  {/* Free value */}
                  <div
                    style={{
                      padding: "10px 16px",
                      textAlign: "center",
                      color: item.free === "CROSS" ? "#EF4444" : "#cbd5e0",
                      fontSize: "13px",
                      backgroundColor: "rgba(55, 65, 81, 0.5)",
                    }}
                  >
                    {renderCell(item.free)}
                  </div>
                  {/* Premium value */}
                  <div
                    style={{
                      padding: "10px 16px",
                      textAlign: "center",
                      color: item.premium.startsWith("CHECK")
                        ? "#22C55E"
                        : "#e2e8f0",
                      fontSize: "13px",
                      fontWeight: item.premium.startsWith("CHECK")
                        ? "500"
                        : "normal",
                      backgroundColor: "rgba(145, 70, 255, 0.1)",
                    }}
                  >
                    {renderCell(item.premium)}
                  </div>
                </div>
              ))}
            </div>

            {/* Upgrade Button */}
            <div style={{ marginTop: "24px" }}>
              {user.approved !== false ? (
                <>
                  <button
                    onClick={handleUpgradeClick}
                    disabled={processingPayment}
                    style={{
                      width: "100%",
                      padding: "16px 24px",
                      backgroundColor: processingPayment
                        ? "#4a5568"
                        : "#9146FF",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "18px",
                      fontWeight: "bold",
                      cursor: processingPayment ? "not-allowed" : "pointer",
                      transition: "all 0.3s",
                      boxShadow: processingPayment
                        ? "none"
                        : "0 4px 15px rgba(145, 70, 255, 0.4)",
                    }}
                    onMouseEnter={(e) => {
                      if (!processingPayment) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 20px rgba(145, 70, 255, 0.5)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!processingPayment) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 15px rgba(145, 70, 255, 0.4)";
                      }
                    }}
                  >
                    {processingPayment ? (
                      "Processing..."
                    ) : (
                      <>
                        <Icon
                          name="party-popper"
                          size={16}
                          color="white"
                          style={{ verticalAlign: "-2px", marginRight: "4px" }}
                        />
                        Upgrade to Premium - $4.99/month
                      </>
                    )}
                  </button>
                  <p
                    style={{
                      textAlign: "center",
                      color: "#718096",
                      fontSize: "13px",
                      marginTop: "12px",
                      marginBottom: 0,
                    }}
                  >
                    Cancel anytime • Secure payment via Stripe
                  </p>
                </>
              ) : (
                <div
                  style={{
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                    border: "1px solid #f59e0b",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0, marginTop: "2px" }}
                    >
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <div>
                      <h4
                        style={{
                          color: "#f59e0b",
                          margin: "0 0 8px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Account Pending Approval
                      </h4>
                      <p
                        style={{
                          color: "#cbd5e0",
                          margin: 0,
                          fontSize: "13px",
                          lineHeight: "1.5",
                        }}
                      >
                        Upgrades are not available until your account is
                        approved. Due to capacity limitations, your account
                        needs approval first. You will be notified via email
                        when approved.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: "32px",
              backgroundColor: "#1a202c",
              padding: "24px",
              borderRadius: "8px",
              border: "1px solid #2d3748",
              maxWidth: "800px",
              margin: "32px auto 0",
            }}
          >
            <p
              style={{
                color: "#cbd5e0",
                fontSize: "14px",
                lineHeight: "1.6",
                margin: 0,
                textAlign: "center",
              }}
            >
              <span style={{ fontWeight: "bold" }}>
                HAL is built to be the smartest, most fun, and most engaging
                chatbot on Twitch.
              </span>{" "}
              It&apos;s a solo passion project — your premium subscription
              directly fuels server costs, AI services, and ongoing upgrades.
              Every bit of support keeps HAL alive and evolving.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Premium/Admin User - Show account overview
  return (
    <div style={{ maxWidth: "800px" }}>
      <h3 style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}>
        Account Overview
      </h3>

      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "24px",
          borderRadius: "8px",
          border: "1px solid #4a5568",
          marginBottom: "20px",
        }}
      >
        <h4
          style={{ color: "#9146FF", margin: "0 0 16px 0", fontSize: "18px" }}
        >
          <Icon
            name="sparkles"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Premium Account Active
        </h4>
        <p
          style={{ color: "#10B981", fontWeight: "bold", marginBottom: "12px" }}
        >
          Thank you for being a Premium subscriber!
        </p>
        <p style={{ color: "#cbd5e0", lineHeight: "1.6" }}>
          You have full access to all HAL8580 features. Manage your subscription
          and explore all the premium features available to you.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="smile"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Premium Personalities
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Access to all personalities including DJ, Musician, Synth Nerd, and
            more.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="wrench"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Custom Personality
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Fully customize HAL&apos;s personality and behavior for your stream.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="bar-chart-3"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Daily Summaries
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Get insights about your audience&apos;s interests and chat trends.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="key"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            API Access
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Full API access for real-time data integration and custom apps.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="brain"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Intelligent Responses
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Automated intelligent response detection based on context analytics.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="book-open"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Knowledge Base
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Up to 100 knowledge items to teach HAL about your stream.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="zap"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Trigger Management
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Up to 100 triggers for custom bot responses and actions.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "20px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h5
            style={{ color: "#e2e8f0", margin: "0 0 12px 0", fontSize: "16px" }}
          >
            <Icon
              name="message-square"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Premium Support
          </h5>
          <p style={{ color: "#a0aec0", fontSize: "14px", margin: 0 }}>
            Get priority support from our team to help you make the most of HAL.
          </p>
        </div>
      </div>

      <div
        style={{
          marginTop: "32px",
          backgroundColor: "#1a202c",
          padding: "24px",
          borderRadius: "8px",
          border: "1px solid #2d3748",
          maxWidth: "800px",
          margin: "32px auto 0",
        }}
      >
        <p
          style={{
            color: "#cbd5e0",
            fontSize: "14px",
            lineHeight: "1.6",
            margin: 0,
            textAlign: "center",
          }}
        >
          <span style={{ fontWeight: "bold" }}>
            HAL is built to be the smartest, most fun, and most engaging chatbot
            on Twitch.
          </span>{" "}
          It&apos;s a solo passion project — your premium subscription directly
          fuels server costs, AI services, and ongoing upgrades. Every bit of
          support keeps HAL alive and evolving.
        </p>
      </div>
    </div>
  );
};

export default AccountSettings;
